import { Dialog, Div, Icon, P } from '@dnb/eufemia';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import { question as help } from '@dnb/eufemia/icons';
import { JwtErrorCode, validateJwt as validateJwtUtil } from '@portals/shared/common/utils';
import { useCallback } from 'react';

import { HelpMarkdown } from '../../../HelpMarkdown';

import style from './index.module.css';

type Props = {
  formId: string;
  onValidationChange?: (errors: JwtErrorCode[], token: string) => void;
};

const placeholderText =
  'Paste your signed JWT here. We will validate its format, audience, expiration, and signature in real-time to prevent errors before submission.';

export function JwtField(props: Props) {
  const { getValue } = Form.useData(props.formId);
  const { onValidationChange } = props;

  const validate = useCallback(
    async (token: string): Promise<Error | undefined> => {
      if (!token?.trim()) {
        onValidationChange?.([], '');
        return;
      }

      const errors = await validateJwtUtil(token);
      onValidationChange?.(errors, token);

      if (errors.length > 0) {
        return new Error();
      }

      return;
    },
    [onValidationChange],
  );

  const environment = getValue('/environment') as string;

  return (
    <Div className={style['FieldStringWrapper']}>
      <Div className={style['DialogHelp']}>
        <P>
          Signed JWT{' '}
          {!environment && <span className={style['EnvironmentWarning']}>Please select an environment first</span>}
        </P>
        <Dialog
          title="How to generate a valid JWT"
          triggerAttributes={{
            icon: <Icon icon={help} />,
            variant: 'secondary',
            size: 'small',
          }}
        >
          <HelpMarkdown
            path={`/content/tpp/${DEVELOPER_PORTAL_TEAM_ID}/create-client-internal/generate-jwt?includeParent=true`}
          />
        </Dialog>
      </Div>
      <Field.String
        disabled={!environment}
        multiline
        onChangeValidator={validate}
        path="/jwks"
        placeholder={placeholderText}
        required
        rows="3"
        size="large"
        width="stretch"
      />
    </Div>
  );
}
